import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { API, mediaUrl } from "@/lib/api";
import { sectionLabel, localize, UI, SPECTRALOOM_URL } from "@/lib/constants";
import { HeroBanner } from "@/components/HeroBanner";
import { SocialEmbeds } from "@/components/SocialEmbed";
import { ArticleBody } from "@/components/ArticleBody";
import { ShareButtons } from "@/components/ShareButtons";
import { Footer } from "@/components/Footer";

export default function ContentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lang, setLang] = useState(sessionStorage.getItem("sl_lang") || "IT");
  const t = UI[lang];

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/content/${id}`)
      .then((r) => setItem(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0B0D12] flex flex-col items-center justify-center gap-4 text-center px-6">
        <p className="text-[#94A3B8]">Content not found.</p>
        <button data-testid="back-home" onClick={() => navigate("/")} className="text-[#8B5CF6] font-medium">
          {UI.IT.back}
        </button>
      </div>
    );
  }

  if (!item) {
    return <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center text-[#64748B]">Loading…</div>;
  }

  const cover = mediaUrl(item.cover_image_url);
  const title = localize(item.title, lang);
  const body = localize(item.body, lang);
  const meta = localize(item.meta_description, lang);
  const dateStr = item.published_at || item.created_at;
  const hasIt = !!(item.title?.it || item.body?.it);
  const hasEn = !!(item.title?.en || item.body?.en);
  const bothLangs = hasIt && hasEn;

  return (
    <div className="min-h-screen bg-[#0B0D12]">
      <title>{title} — Spectraloom</title>
      <meta name="description" content={meta} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={meta} />
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="Spectraloom Blog" />
      {cover && <meta property="og:image" content={cover} />}

      <header className="sticky top-0 z-40 border-b border-[#2D3748] bg-[#0B0D12]/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src="/spectraloom-logo.jpg" alt="Spectraloom Blog" className="h-10 w-10 rounded-lg object-contain bg-[#0B0D12]" />
            <span className="font-heading text-lg font-bold tracking-tight text-white">Spectraloom Blog</span>
          </a>
          <div className="flex items-center gap-4">
            <button
              data-testid="back-to-blog"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> {t.back}
            </button>
            <a
              href={SPECTRALOOM_URL}
              data-testid="back-to-app"
              className="inline-flex items-center gap-2 rounded-full border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 px-3.5 py-1.5 text-xs font-semibold text-[#a78bfa] transition-colors hover:bg-[#8B5CF6]/20 hover:text-white"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> {t.backToApp}
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6">
        <HeroBanner lang={lang} />

        <article data-testid="article-detail" className="max-w-3xl mx-auto py-6">
          <div className="flex flex-wrap items-center gap-2.5 mb-5">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#8B5CF6]/15 text-[#a78bfa] border border-[#8B5CF6]/30">
              {sectionLabel(item.category, lang)}
            </span>
            {bothLangs && (
              <div data-testid="detail-lang-selector" className="flex items-center rounded-full border border-[#06B6D4]/30 bg-[#06B6D4]/5 p-1">
                {["IT", "EN"].map((l) => (
                  <button
                    key={l}
                    data-testid={`detail-lang-${l}`}
                    onClick={() => { setLang(l); sessionStorage.setItem("sl_lang", l); }}
                    className={`px-3 py-1 rounded-full text-xs font-mono font-semibold transition-colors ${lang === l ? "bg-[#06B6D4] text-[#0B0D12]" : "text-[#06B6D4] hover:bg-[#06B6D4]/10"}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.08]">
            {title}
          </h1>

          <div className="flex items-center gap-5 mt-5 text-sm text-[#64748B]">
            <span className="flex items-center gap-1.5">
              <User className="w-4 h-4" /> {item.author || "Spectraloom Staff"}
            </span>
            {dateStr && (
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {new Date(dateStr).toLocaleDateString(lang === "IT" ? "it-IT" : "en-US", {
                  day: "numeric", month: "long", year: "numeric",
                })}
              </span>
            )}
          </div>

          <div className="mt-6">
            <ShareButtons slug={item.slug} title={title} lang={lang} />
          </div>

          {cover && (
            <div className="mt-8 rounded-2xl overflow-hidden border border-[#2D3748]">
              <img src={cover} alt={title} className="w-full object-cover" />
            </div>
          )}

          <SocialEmbeds links={item.social_links} />

          <ArticleBody html={body} testId="article-body" className="prose-sl mt-8" />

          <div data-testid="article-signature" className="mt-12 pt-6 border-t border-[#2D3748] flex items-center gap-3">
            <img src="/spectraloom-logo-round.jpg" alt="Spectraloom Staff" className="w-11 h-11 rounded-full object-cover shrink-0" />
            <div>
              <p className="font-heading text-sm font-semibold text-white leading-tight">Spectraloom Staff</p>
              <p className="text-xs text-[#64748B]">
                {lang === "IT" ? "Pubblicato il" : "Published on"}{" "}
                {dateStr && new Date(dateStr).toLocaleDateString(lang === "IT" ? "it-IT" : "en-US", {
                  day: "numeric", month: "long", year: "numeric",
                })}
              </p>
            </div>
          </div>

          <p data-testid="article-ai-disclosure" className="mt-6 italic" style={{ fontSize: "0.8em", opacity: 0.45, color: "#64748B" }}>
            {(UI[lang] || UI.IT).aiDisclosure}
          </p>
        </article>
      </main>

      <Footer lang={lang} />
    </div>
  );
}
